import {
	getLivestockKpiSummaryQueryOptions,
	getOverdueTasksSummaryQueryOptions,
} from "@packages/api-client/hooks/analyticsController";
import {
	countPendingAssistantProposalsQueryOptions,
	listAssistantProposalsQueryOptions,
} from "@packages/api-client/hooks/assistantProposalsController";
import { getEquipmentDashboardQueryOptions } from "@packages/api-client/hooks/equipmentController";
import { getLivestockDashboardQueryOptions } from "@packages/api-client/hooks/livestockController";
import { getFinishedCattleMarketPricesQueryOptions } from "@packages/api-client/hooks/marketPricesController";
import {
	listObjectivesQueryOptions,
	listTasksQueryOptions,
} from "@packages/api-client/hooks/planningTasksController";
import type { ListAssistantProposalsQueryParams } from "@packages/api-client/types/assistantProposalsController";
import type { FarmScope } from "@/hooks/useFarmScope";

/**
 * Scope-aware query options.
 *
 * Given the active {@link FarmScope}, pick the org-level ("All Farms") endpoint
 * or the farm-level endpoint for an entity. Pages then issue a single
 * `useQuery(scopedXQueryOptions(orgId, scope))` instead of re-implementing the
 * dual-`useQuery` + `enabled` branch by hand — the DRY payoff of modelling
 * "all" as a first-class scope.
 *
 * The org-level endpoints aggregate across every farm server-side, so the
 * "all" scope intentionally ignores `scope.establishmentIds` here; the ids are
 * only meaningful to UI that enumerates farms (e.g. the farm grid).
 *
 * Single farm vs all farms is now one endpoint per entity: the org-level
 * endpoint takes an optional `?establishmentId=` (present → single farm;
 * absent → all farms), so both scopes share a response shape and cache key
 * family. See docs/KeyDecisions/2026-05-23-farmscope-backend-filtering-standard.md.
 */
export const scopedTasksQueryOptions = (orgId: string, scope: FarmScope) =>
	listTasksQueryOptions(
		orgId,
		scope.kind === "single" ? { establishmentId: scope.establishmentId } : {},
	);

export const scopedObjectivesQueryOptions = (orgId: string, scope: FarmScope) =>
	listObjectivesQueryOptions(
		orgId,
		scope.kind === "single" ? { establishmentId: scope.establishmentId } : {},
	);

/**
 * Assistant proposals scoped to the active farm — one helper for the
 * proposals page and its loader so the scope-to-`establishmentId` mapping
 * lives here, not in each consumer.
 */
export const scopedAssistantProposalsQueryOptions = (
	orgId: string,
	scope: FarmScope,
	filters: Omit<ListAssistantProposalsQueryParams, "establishmentId"> = {},
) =>
	listAssistantProposalsQueryOptions(orgId, {
		...filters,
		...(scope.kind === "single"
			? { establishmentId: scope.establishmentId }
			: {}),
	});

/** Pending assistant-proposal count within the active farm scope (home card). */
export const scopedPendingAssistantProposalCountQueryOptions = (
	orgId: string,
	scope: FarmScope,
) =>
	countPendingAssistantProposalsQueryOptions(
		orgId,
		scope.kind === "single"
			? { establishmentId: scope.establishmentId }
			: undefined,
	);

/** Livestock KPI rollup (head count, herds, 30-day record counts) for a scope. */
export const scopedLivestockDashboardQueryOptions = (
	orgId: string,
	scope: FarmScope,
) =>
	getLivestockDashboardQueryOptions(
		orgId,
		scope.kind === "single" ? { establishmentId: scope.establishmentId } : {},
	);

/**
 * Livestock KPI summary (growth, stocking, reproduction, mortality) for a
 * scope. All four sections by default — the home card reads every one, so the
 * `?sections=` subset stays unused here.
 */
export const scopedLivestockKpiSummaryQueryOptions = (
	orgId: string,
	scope: FarmScope,
) =>
	getLivestockKpiSummaryQueryOptions(
		orgId,
		scope.kind === "single" ? { establishmentId: scope.establishmentId } : {},
	);

/**
 * The caller's calendar day as "YYYY-MM-DD", in LOCAL time. The overdue-task
 * count is taken against this day: a farm in Brazil is hours behind UTC, and
 * a task due today must not turn overdue at 21:00 because the server's day
 * already rolled over.
 */
function localIsoDay(now: Date = new Date()): string {
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const day = String(now.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

/**
 * Overdue task count, the oldest overdue task and the farms affected — the
 * home dashboard's "Tasks overdue" tile and grouped row. `today` is part of
 * the key, so a tab left open overnight re-reads on the new day.
 */
export const scopedOverdueTasksQueryOptions = (
	orgId: string,
	scope: FarmScope,
	today: string = localIsoDay(),
) =>
	getOverdueTasksSummaryQueryOptions(orgId, {
		today,
		...(scope.kind === "single"
			? { establishmentId: scope.establishmentId }
			: {}),
	});

/** Fleet KPI rollup (maintenance due, open work, uptime) for a scope. */
export const scopedEquipmentDashboardQueryOptions = (
	orgId: string,
	scope: FarmScope,
) =>
	getEquipmentDashboardQueryOptions(
		orgId,
		scope.kind === "single" ? { establishmentId: scope.establishmentId } : {},
	);

/**
 * The route answers with `Cache-Control: private, max-age=3600`; the query's
 * `staleTime` mirrors it so a dashboard revisit inside that hour reads from
 * cache instead of re-asking for a number that only moves weekly.
 */
const MARKET_PRICE_STALE_TIME_MS = 3600 * 1000;

/** Published finished-cattle quote per state, for the farms in scope. */
export const scopedMarketPriceQueryOptions = (
	orgId: string,
	scope: FarmScope,
) => ({
	...getFinishedCattleMarketPricesQueryOptions(
		orgId,
		scope.kind === "single" ? { establishmentId: scope.establishmentId } : {},
	),
	staleTime: MARKET_PRICE_STALE_TIME_MS,
});
